import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppCategory, AppColorViewModel } from "client/Api/Apps/common";
import { TagViewModel } from "client/Api/Tags/tags";
import {
	HighlightItem,
	MaintenanceWindowAppModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	AppIcon,
	Highlight,
	HighlightedText,
	Icon,
	ListItem,
	TagList,
	TagModel,
	Typography
} from "client/Components";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import classNames from "classnames";
import styles from "./AppListItem.module.less";
import { ReactElement, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { categoryToNameMap } from "client/Features/Apps/Store/CategoryFilter";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";

export type AppListItemProps = {
	id: number;
	name: string;
	color: AppColorViewModel;
	image: AppImageDisplayInfo;
	category: AppCategory;
	maintenanceWindow: MaintenanceWindowAppModel | null;
	tags: readonly TagViewModel[];
	hitHighlights?: readonly HighlightItem[];
	tagsHitHighlights?: readonly (readonly HighlightItem[])[];
	onRemove?: () => void;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const AppListItem = (props: AppListItemProps): ReactElement => {
	const {
		id,
		category,
		color,
		image,
		maintenanceWindow,
		name,
		tags,
		hitHighlights = EMPTY_ARRAY,
		tagsHitHighlights,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const { colorClass, colorStyle } = getReactColorAttributes(color);

	const isInMaintenance: boolean = isNotNil(maintenanceWindow);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	const tagModels = useMemo(
		(): readonly TagModel<number>[] => {
			let checkedTagsHitHighlights: readonly (readonly HighlightItem[])[] | undefined = tagsHitHighlights;

			if (isNotNil(checkedTagsHitHighlights) && tags.length !== checkedTagsHitHighlights.length) {
				console.error("Collection of tags and collection with highlights for each tag do not match in length. Highlights will be ignored.");
				checkedTagsHitHighlights = undefined;
			}

			return tags.map(
				(tag, index): TagModel<number> => ({
					text: tag.name,
					value: tag.id,
					highlight: isNotNil(checkedTagsHitHighlights)
						? getTagNameHighlight(checkedTagsHitHighlights[index])
						: undefined,
					color: "secondary"
				})
			);
		},
		[tags, tagsHitHighlights]
	);

	return (
		<ListItem
			trackingId={generateTrackingId("app-result-list-item", `|${id}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div
						style={colorStyle}
						className={classNames(
							colorClass,
							styles.appIconWrapper,
							{
								[styles.appIconWrapper_isInMaintenance]: isInMaintenance,
							}
						)}
					>
						<div className={styles.appIcon}>
							<AppIcon
								value={image}
								color="inherit" />
						</div>
					</div>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={name}
							highlight={highlights.get("name")}
						/>
					</Typography>
					{isInMaintenance && (
						<span className={styles.maintenanceIconWrapper}>
							<Icon
								value={{
									name: "screwdriver-wrench",
									color: "danger",
									kind: "font"
								}}

							/>
						</span>
					)}
				</ListItem.Title>
				<div className={styles.infoRow}>
					<ListItem.SubTitle>
						<span {...setTrackingId("category")}>
							{localize(categoryToNameMap[category])}
						</span>
					</ListItem.SubTitle>
					{tags.length > 0 && (
						<>
							<ListItem.SubTitle>|</ListItem.SubTitle>
							<div
								className={styles.tagsContainer}
								onClick={e => e.stopPropagation()}
							>
								<TagList
									tags={tagModels}
									size="small"
									showMoreTagsColor="secondary"
								/>
							</div>
						</>
					)}
				</div>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
AppListItem.displayName = "AppListItem";

function getTagNameHighlight(highlights: readonly HighlightItem[]): Highlight | undefined {
	const hitHighlight: HighlightItem | undefined = highlights.find(h => h.highlightedField === "name");

	if (!hitHighlight) {
		return undefined;
	}

	return [
		hitHighlight.startPosition,
		hitHighlight.fragmentLength
	];
}
